/**
 * ERP fixed-asset-register header dictionary + shipped presets (SIL-265,
 * Wave 2 T5 of resources/plans/erp-register-import-spec.md, decision 6 /
 * §3.2 security review).
 *
 * Single source of truth for:
 *   - the closed set of TARGET fields the column-mapper wizard can map a
 *     source ERP column onto (`ErpRegisterTargetFieldSchema` — a Zod enum,
 *     never a free string, so a mapping can never be turned into an
 *     identifier or interpolated into SQL — spec §3.2 "target fields are a
 *     closed Zod enum, never interpolated as identifiers");
 *   - the shipped per-ERP header presets (NetSuite, SAP, Dynamics 365, Sage
 *     Intacct) the deterministic auto-suggest matches against;
 *   - the header count/length caps enforced BEFORE auto-suggest runs
 *     (spec §3.2: ≤256 headers, ≤512 chars each).
 *
 * Consumers (Wave 3+): the mapping-template CRUD route, the column-mapper
 * wizard UI, and the drift test alongside this file — the same
 * three-consumer, single-file convention as `import-template.ts` (SIL-15).
 *
 * Deliberately pure TS (no DB import, no file-format library — spec §3.3
 * module-boundary rule: "packages/shared holds ONLY pure matching math and
 * header dictionaries ... zero file-format libraries in shared") so it is
 * safe to import from the Lambda, the parse worker, and the browser alike.
 *
 * SECURITY (spec §3.2): a source header is always DATA, never an
 * identifier — dictionaries below only ever compare a normalized header
 * STRING against other STRINGS (never used to build a column name, SQL
 * fragment, or object accessed via a caller-controlled key path). The raw
 * header the customer's ERP shipped is stored verbatim wherever a mapping is
 * persisted; only the resolved `targetField` (a member of the closed enum)
 * ever drives a write.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Target fields — closed enum. Adding a field here is the ONLY way a mapping
// wizard column can ever land somewhere new; nothing downstream accepts an
// arbitrary string.
// ---------------------------------------------------------------------------

/**
 * Match-waterfall keys (decision 3) plus the ingest facts decision 5 locks in
 * ("purchase price/dates/PO plus erp_reported_nbv/erp_nbv_as_of, and
 * method/life when present") plus the lump-line quantity column (prototype
 * learning 2) and a free-text description used only for triage
 * identification, never matching.
 */
export const ERP_REGISTER_TARGET_FIELDS = [
  'serial_number',
  'asset_tag',
  'po_ref',
  'invoice_ref',
  'quantity',
  'description',
  'purchase_price_usd',
  'purchased_at',
  'in_service_at',
  'erp_reported_nbv',
  'erp_nbv_as_of',
  'depreciation_method',
  'useful_life_years',
] as const;

export const ErpRegisterTargetFieldSchema = z.enum(ERP_REGISTER_TARGET_FIELDS);
export type ErpRegisterTargetField = z.infer<typeof ErpRegisterTargetFieldSchema>;

// ---------------------------------------------------------------------------
// Header caps — enforced BEFORE auto-suggest ever runs (spec §3.2 DoS /
// data-minimization guard: an ERP export column list is small in practice;
// anything past this is either a malformed file or an abuse attempt).
// ---------------------------------------------------------------------------

export const ERP_IMPORT_MAX_HEADERS = 256;
export const ERP_IMPORT_MAX_HEADER_LENGTH = 512;

export class ErpHeaderCapExceededError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ErpHeaderCapExceededError';
  }
}

/**
 * Throws `ErpHeaderCapExceededError` if the header row exceeds either cap.
 * Callers (Wave 3 parse/mapping-template routes) MUST call this before
 * `suggestErpHeaderMapping` — auto-suggest never runs on an unchecked header
 * row.
 */
export function assertErpHeaderCaps(headers: readonly string[]): void {
  if (headers.length > ERP_IMPORT_MAX_HEADERS) {
    throw new ErpHeaderCapExceededError(
      `Register export has ${headers.length} columns, exceeding the ${ERP_IMPORT_MAX_HEADERS}-header cap.`,
    );
  }
  for (const header of headers) {
    if (header.length > ERP_IMPORT_MAX_HEADER_LENGTH) {
      throw new ErpHeaderCapExceededError(
        `A column header is ${header.length} characters, exceeding the ${ERP_IMPORT_MAX_HEADER_LENGTH}-character cap.`,
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Shipped presets — one dictionary per ERP brand (decision 6: "shipped
// header-dictionary presets for NetSuite/SAP/Dynamics/Sage Intacct"). Every
// entry is a HARDCODED, deterministic list of known real-world header
// spellings for that ERP's fixed-asset-register export — no fuzzy/ML
// matching (decision 6: "No LLM parsing — auditability requires deterministic
// mapping").
// ---------------------------------------------------------------------------

export const ERP_SOURCE_SYSTEMS = ['netsuite', 'sap', 'dynamics_365', 'sage_intacct'] as const;
export const ErpSourceSystemSchema = z.enum(ERP_SOURCE_SYSTEMS);
export type ErpSourceSystem = z.infer<typeof ErpSourceSystemSchema>;

export interface ErpHeaderDictionaryEntry {
  targetField: ErpRegisterTargetField;
  /** Known header spellings for this ERP, exactly as they appear in a real export. */
  headers: readonly string[];
}

/** Lowercase, trim, and collapse whitespace/underscores for header comparison. */
export function normalizeErpHeader(header: string): string {
  return header
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, ' ');
}

const NETSUITE_HEADERS: readonly ErpHeaderDictionaryEntry[] = [
  { targetField: 'asset_tag', headers: ['Asset Number', 'Asset #', 'Fixed Asset Number'] },
  { targetField: 'description', headers: ['Asset Name', 'Asset Description', 'Description'] },
  { targetField: 'serial_number', headers: ['Serial Number', 'Serial #'] },
  { targetField: 'po_ref', headers: ['PO Number', 'Purchase Order', 'PO #'] },
  { targetField: 'invoice_ref', headers: ['Invoice Number', 'Invoice #'] },
  { targetField: 'quantity', headers: ['Quantity', 'Qty'] },
  { targetField: 'purchase_price_usd', headers: ['Purchase Price', 'Original Cost', 'Cost Basis'] },
  { targetField: 'purchased_at', headers: ['Purchase Date', 'Acquisition Date'] },
  { targetField: 'in_service_at', headers: ['In Service Date', 'Placed in Service Date'] },
  { targetField: 'erp_reported_nbv', headers: ['Net Book Value', 'NBV'] },
  { targetField: 'erp_nbv_as_of', headers: ['NBV As Of', 'Book Value Date'] },
  { targetField: 'depreciation_method', headers: ['Depreciation Method'] },
  { targetField: 'useful_life_years', headers: ['Useful Life', 'Useful Life (Years)'] },
];

const SAP_HEADERS: readonly ErpHeaderDictionaryEntry[] = [
  { targetField: 'asset_tag', headers: ['Asset', 'Main Asset Number', 'Asset Number'] },
  { targetField: 'description', headers: ['Asset Description', 'Description'] },
  { targetField: 'serial_number', headers: ['Serial Number', 'Serial No.'] },
  { targetField: 'po_ref', headers: ['Purchase Order', 'PO Number'] },
  { targetField: 'invoice_ref', headers: ['Reference Document', 'Invoice Number'] },
  { targetField: 'quantity', headers: ['Quantity', 'Qty'] },
  { targetField: 'purchase_price_usd', headers: ['Acquisition Value', 'Acquisition and Production Costs (APC)'] },
  { targetField: 'purchased_at', headers: ['Capitalized on', 'Capitalization Date'] },
  { targetField: 'in_service_at', headers: ['Asset Value Date'] },
  { targetField: 'erp_reported_nbv', headers: ['Net Book Value', 'Book Value'] },
  { targetField: 'erp_nbv_as_of', headers: ['Report Date', 'Valuation Date'] },
  { targetField: 'depreciation_method', headers: ['Depreciation Key'] },
  { targetField: 'useful_life_years', headers: ['Planned Useful Life', 'Useful Life in Years'] },
];

const DYNAMICS_365_HEADERS: readonly ErpHeaderDictionaryEntry[] = [
  { targetField: 'asset_tag', headers: ['Fixed asset number', 'Asset number'] },
  { targetField: 'description', headers: ['Description', 'Fixed asset description'] },
  { targetField: 'serial_number', headers: ['Serial number'] },
  { targetField: 'po_ref', headers: ['Purchase order', 'Purchase order number'] },
  { targetField: 'invoice_ref', headers: ['Invoice', 'Invoice number'] },
  { targetField: 'quantity', headers: ['Quantity'] },
  { targetField: 'purchase_price_usd', headers: ['Acquisition price', 'Acquisition cost'] },
  { targetField: 'purchased_at', headers: ['Acquisition date'] },
  { targetField: 'in_service_at', headers: ['Depreciation start date'] },
  { targetField: 'erp_reported_nbv', headers: ['Net book value'] },
  { targetField: 'erp_nbv_as_of', headers: ['Value as of date'] },
  { targetField: 'depreciation_method', headers: ['Depreciation profile'] },
  { targetField: 'useful_life_years', headers: ['Life', 'Life (years)'] },
];

const SAGE_INTACCT_HEADERS: readonly ErpHeaderDictionaryEntry[] = [
  { targetField: 'asset_tag', headers: ['Asset ID', 'Asset No.'] },
  { targetField: 'description', headers: ['Asset description', 'Description'] },
  { targetField: 'serial_number', headers: ['Serial number'] },
  { targetField: 'po_ref', headers: ['Purchase order number', 'PO number'] },
  { targetField: 'invoice_ref', headers: ['Invoice number'] },
  { targetField: 'quantity', headers: ['Quantity'] },
  { targetField: 'purchase_price_usd', headers: ['Cost', 'Purchase cost'] },
  { targetField: 'purchased_at', headers: ['Purchase date'] },
  { targetField: 'in_service_at', headers: ['In-service date'] },
  { targetField: 'erp_reported_nbv', headers: ['Book value', 'Net book value'] },
  { targetField: 'erp_nbv_as_of', headers: ['As of date'] },
  { targetField: 'depreciation_method', headers: ['Depreciation method'] },
  { targetField: 'useful_life_years', headers: ['Useful life', 'Life (years)'] },
];

export const ERP_HEADER_PRESETS: Readonly<Record<ErpSourceSystem, readonly ErpHeaderDictionaryEntry[]>> = {
  netsuite: NETSUITE_HEADERS,
  sap: SAP_HEADERS,
  dynamics_365: DYNAMICS_365_HEADERS,
  sage_intacct: SAGE_INTACCT_HEADERS,
};

// ---------------------------------------------------------------------------
// Deterministic auto-suggest — exact, normalized string match only. No
// fuzzy/similarity scoring: a header either matches a known spelling or it
// doesn't, so the same file always produces the same suggestion (decision 6
// auditability requirement).
// ---------------------------------------------------------------------------

/**
 * Suggests a target field for every source header, or `null` when nothing in
 * the dictionary matches (the wizard leaves those columns unmapped for the
 * user to pick by hand). Caller MUST have already run `assertErpHeaderCaps`.
 *
 * When `system` is given its preset is tried first; every other preset is
 * tried after as a fallback (real customer exports occasionally rename a
 * column away from their ERP's own default). First match wins, in the fixed
 * `ERP_SOURCE_SYSTEMS` order — deterministic, never a coin flip.
 */
export function suggestErpHeaderMapping(
  sourceHeaders: readonly string[],
  system?: ErpSourceSystem,
): Readonly<Record<string, ErpRegisterTargetField | null>> {
  // Defense in depth: callers are documented to check this first, but
  // auto-suggest never runs on an unchecked header row even if they forget.
  assertErpHeaderCaps(sourceHeaders);

  const order: readonly ErpSourceSystem[] = system
    ? [system, ...ERP_SOURCE_SYSTEMS.filter((s) => s !== system)]
    : ERP_SOURCE_SYSTEMS;

  const result: Record<string, ErpRegisterTargetField | null> = {};
  for (const header of sourceHeaders) {
    const normalized = normalizeErpHeader(header);
    let match: ErpRegisterTargetField | null = null;
    for (const sys of order) {
      const found = ERP_HEADER_PRESETS[sys].find((entry) =>
        entry.headers.some((known) => normalizeErpHeader(known) === normalized),
      );
      if (found) {
        match = found.targetField;
        break;
      }
    }
    result[header] = match;
  }
  return result;
}
